import { registerOverlay } from 'klinecharts'
import { isNum, type Coord, type OverlayFigure } from './overlayUtils'
import { NO_DEFAULT_FIGURES, type OverlayTemplateParam } from './registrationUtils'

/**
 * Locked marker for one fill of a backtest trade, placed at the fill's price
 * on the bar its timestamp resolves to. Drawn to match the Build page's
 * transaction arrows (backtest-chart.tsx) so a trade reads the same on both
 * surfaces: a buy is an up-pointing triangle hanging just below the price,
 * a sell a down-pointing one just above it, pure-saturated green / red with
 * a 1px black outline so they stay readable over a same-coloured candle.
 *
 * Pass `extendData` as `{ side: 'buy' | 'sell', color? }`; `color` overrides
 * the fill for a host with a reason to.
 */
export const TRADE_FILL_MARKER_NAME = 'tradeFillMarker'

export interface TradeFillMarkerData {
  side: 'buy' | 'sell'
  color?: string
}

// Same geometry as the Build chart's arrows: 6px half-width, 12px tall, the
// apex 2px past the price toward the bar and the base 10px away from it.
const HALF_WIDTH = 6
const APEX_OVERSHOOT = 2
const BASE_OFFSET = 10
export const TRADE_FILL_BUY_COLOR = '#00FF00'
export const TRADE_FILL_SELL_COLOR = '#FF0000'
const OUTLINE_COLOR = '#000000'

/** Pure figure builder, exported for tests. */
export function buildTradeFillFigures(
  at: Coord,
  data: Partial<TradeFillMarkerData>,
): OverlayFigure[] {
  if (!isNum(at.x) || !isNum(at.y)) return []
  const isBuy = data.side !== 'sell'
  // A buy sits below the price and points up at it; a sell mirrors that.
  const dir = isBuy ? 1 : -1
  const coordinates = [
    { x: at.x, y: at.y - dir * APEX_OVERSHOOT },
    { x: at.x - HALF_WIDTH, y: at.y + dir * BASE_OFFSET },
    { x: at.x + HALF_WIDTH, y: at.y + dir * BASE_OFFSET },
  ]
  const color = data.color ?? (isBuy ? TRADE_FILL_BUY_COLOR : TRADE_FILL_SELL_COLOR)
  // One figure, filled and outlined. klinecharts strokes a polygon with
  // `borderColor` / `borderSize`, not `color`: a separate stroke figure
  // keyed on `color` fell through to the theme's default (blue) border.
  return [
    {
      type: 'polygon',
      attrs: { coordinates },
      styles: { style: 'stroke_fill', color, borderColor: OUTLINE_COLOR, borderSize: 1 },
      ignoreEvent: true,
    },
  ]
}

export function registerTradeFillMarkerOverlay(): void {
  registerOverlay({
    name: TRADE_FILL_MARKER_NAME,
    totalStep: 2,
    ...NO_DEFAULT_FIGURES,
    createPointFigures: ({ coordinates, overlay }) => {
      if (coordinates.length === 0) return []
      const data = (overlay.extendData as Partial<TradeFillMarkerData> | undefined) ?? {}
      return buildTradeFillFigures(coordinates[0], data)
    },
  } as OverlayTemplateParam)
}
